import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { auth } from "../firebase";
import moment from "moment";

export default function AdminSecurityMonitor() {
    const [securityStats, setSecurityStats] = useState(null);
    const [failedLogins, setFailedLogins] = useState([]);
    const [suspiciousActivities, setSuspiciousActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchSecurityData();
        
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchSecurityData, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchSecurityData = async () => {
        if (!loading) setRefreshing(true);
        
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = { Authorization: `Bearer ${token}` };

            const [statsRes, loginsRes, activitiesRes] = await Promise.all([
                axios.get("/api/security/stats", { headers }),
                axios.get("/api/security/failed-logins", { headers }),
                axios.get("/api/security/suspicious-activities", { headers })
            ]);

            setSecurityStats(statsRes.data);
            setFailedLogins(loginsRes.data);
            setSuspiciousActivities(activitiesRes.data);
        } catch (error) {
            console.error("Error fetching security data:", error);
            toast.error("Failed to load security data");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleUnblockIP = async (ipAddress) => {
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.post("/api/security/unblock-ip", 
                { ip_address: ipAddress },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`IP ${ipAddress} has been unblocked`);
            fetchSecurityData(); // Refresh data
        } catch (error) {
            toast.error(`Failed to unblock IP: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleCleanupLogs = async () => {
        if (!window.confirm("This will delete security logs older than 30 days. Continue?")) {
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await axios.post("/api/security/cleanup", {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const data = response.data.data;
            if (data.deleted_count > 0) {
                toast.success(`Cleaned up ${data.deleted_count} old log entries`);
            } else {
                toast.info("No old logs to clean up");
            }
            
            fetchSecurityData(); // Refresh data
        } catch (error) {
            toast.error(`Failed to cleanup logs: ${error.response?.data?.detail || error.message}`);
        }
    };

    const getStatusBadgeClass = (count, threshold) => {
        if (count === 0) return "badge bg-success";
        if (count <= threshold) return "badge bg-warning";
        return "badge bg-danger";
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading security data...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">🔒 Security Monitor</h4>
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-outline-secondary btn-sm" 
                        onClick={fetchSecurityData}
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Refreshing...
                            </>
                        ) : (
                            "🔄 Refresh"
                        )}
                    </button>
                    <button 
                        className="btn btn-outline-warning btn-sm" 
                        onClick={handleCleanupLogs}
                        title="Clean up security logs older than 30 days"
                    >
                        🧹 Cleanup Logs
                    </button>
                    <small className="text-muted align-self-center">
                        Last updated: {securityStats?.last_update ? moment(securityStats.last_update).format("HH:mm:ss") : "Never"}
                    </small>
                </div>
            </div>

            {/* Security Overview Cards */}
            <div className="row mb-4">
                <div className="col-md-2">
                    <div className="card text-center">
                        <div className="card-body">
                            <h5 className="card-title">
                                <span className={getStatusBadgeClass(securityStats?.failed_logins_last_hour || 0, 5)}>
                                    {securityStats?.failed_logins_last_hour || 0}
                                </span>
                            </h5>
                            <p className="card-text small">Failed Logins<br/>(Last Hour)</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="card text-center">
                        <div className="card-body">
                            <h5 className="card-title">
                                <span className={getStatusBadgeClass(securityStats?.blocked_ips_count || 0, 0)}>
                                    {securityStats?.blocked_ips_count || 0}
                                </span>
                            </h5>
                            <p className="card-text small">Blocked IPs</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="card text-center">
                        <div className="card-body">
                            <h5 className="card-title">
                                <span className={getStatusBadgeClass(securityStats?.suspicious_activities_last_24h || 0, 3)}>
                                    {securityStats?.suspicious_activities_last_24h || 0}
                                </span>
                            </h5>
                            <p className="card-text small">Suspicious Activities<br/>(24h)</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card text-center">
                        <div className="card-body">
                            <h5 className="card-title">
                                <span className="badge bg-info">
                                    {securityStats?.total_monitored_ips || 0}
                                </span>
                            </h5>
                            <p className="card-text small">Monitored IPs</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card text-center">
                        <div className="card-body">
                            <h5 className="card-title">
                                <span className="badge bg-primary">
                                    {securityStats?.active_sessions || 0}
                                </span>
                            </h5>
                            <p className="card-text small">Active Sessions</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Failed Login Attempts */}
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">🚨 Failed Login Attempts</h6>
                        </div>
                        <div className="card-body">
                            {failedLogins.length === 0 ? (
                                <p className="text-muted text-center">No recent failed login attempts</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>IP Address</th>
                                                <th>Attempts</th>
                                                <th>Last Attempt</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {failedLogins.map((login, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <code className="small">{login.ip}</code>
                                                    </td>
                                                    <td>
                                                        <span className={getStatusBadgeClass(login.attempt_count, 3)}>
                                                            {login.attempt_count}
                                                        </span>
                                                    </td>
                                                    <td className="small">
                                                        {moment(login.last_attempt).fromNow()}
                                                    </td>
                                                    <td>
                                                        {login.is_blocked ? (
                                                            <span className="badge bg-danger">Blocked</span>
                                                        ) : (
                                                            <span className="badge bg-warning">Monitored</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {login.is_blocked && (
                                                            <button
                                                                className="btn btn-outline-success btn-sm"
                                                                onClick={() => handleUnblockIP(login.ip)}
                                                            >
                                                                Unblock
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Suspicious Activities */}
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">🔍 Recent Security Events</h6>
                        </div>
                        <div className="card-body">
                            {suspiciousActivities.length === 0 ? (
                                <p className="text-muted text-center">No recent security events</p>
                            ) : (
                                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                    {suspiciousActivities.map((activity, index) => (
                                        <div key={index} className="border-bottom pb-2 mb-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <span className={`badge ${activity.action === 'IP_BLOCKED' ? 'bg-danger' : 'bg-success'} me-2`}>
                                                        {activity.action}
                                                    </span>
                                                    <code className="small">{activity.ip}</code>
                                                </div>
                                                <small className="text-muted">
                                                    {moment(activity.timestamp).fromNow()}
                                                </small>
                                            </div>
                                            <small className="text-muted d-block mt-1">
                                                {activity.reason}
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Security Tips */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-info">
                        <div className="card-header bg-info text-white">
                            <h6 className="mb-0">💡 Security Recommendations</h6>
                        </div>
                        <div className="card-body">
                            <ul className="mb-0">
                                <li><strong>Monitor failed logins:</strong> More than 5 failed attempts in an hour may indicate a brute force attack.</li>
                                <li><strong>Blocked IPs:</strong> IPs are automatically blocked after 5 failed attempts. Review and unblock legitimate users if needed.</li>
                                <li><strong>Rate limiting:</strong> All endpoints have rate limiting enabled to prevent abuse.</li>
                                <li><strong>Regular monitoring:</strong> Check this dashboard regularly for security threats.</li>
                                <li><strong>Strong passwords:</strong> Ensure all admin passwords are strong and unique.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
